import Layout from '@/components/Layout';
import SEOHead from '@/components/SEOHead';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

const Section = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[#E5E2DB] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-6 text-left hover:bg-[#F5F3EF] px-4 -mx-4 rounded-lg transition-colors"
      >
        <h2 className="text-xl font-semibold text-[#2B2B2B]">{title}</h2>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#8A9474] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#8A9474] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-6 px-4 text-[#4A4A4A] space-y-4 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

export default function CGV() {
  return (
    <Layout>
      <SEOHead 
        title="Conditions Générales de Vente | Homely Conciergerie"
        description="Conditions générales de vente de Homely Conciergerie - Tarifs, modalités de paiement et conditions contractuelles."
        robots="noindex, follow"
      />
      <div className="min-h-screen bg-[#F5F3EF] py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-[#2B2B2B] mb-4" data-testid="cgv-title">
            Conditions Générales de Vente
          </h1>
          <p className="text-[#4A4A4A] mb-8">
            Version mise à jour le 1er mai 2025
          </p>

          <div className="bg-white rounded-2xl shadow-sm p-8">
            {/* En-tête du contrat */}
            <div className="mb-8 pb-6 border-b border-[#E5E2DB]">
              <h3 className="text-xl font-semibold text-[#2B2B2B] mb-4">ENTRE LES SOUSSIGNÉS</h3>
              <div className="space-y-4 text-[#4A4A4A]">
                <div>
                  <p className="font-semibold text-[#2B2B2B]">LE PROPRIÉTAIRE</p>
                  <p>Selon Mandat de location Saisonnière</p>
                </div>
                <div>
                  <p className="font-semibold text-[#2B2B2B]">LE PRESTATAIRE</p>
                  <p>Homely Solutions Sàrl<br />
                  Jean-André Venel 4<br />
                  1400 Yverdon-les-Bains<br />
                  RC : CHE-418.697.448<br />
                  Email : <a href="mailto:info@homely-solutions.com" className="text-[#8A9474] hover:underline">info@homely-solutions.com</a></p>
                </div>
                <p className="font-semibold text-[#2B2B2B] mt-6">IL A ÉTÉ FAIT ET CONVENU CE QUI SUIT :</p>
              </div>
            </div>

            {/* Sections pliables */}
            <div className="space-y-0">
              <Section title="Article 1 – Objet du Service de Gestion Intégrale" defaultOpen={true}>
                <p>Par ces présentes, le Propriétaire confère à Homely Solutions Sàrl, qui l'accepte, la location en courte durée de son logement ainsi que sa Gestion Intégrale. Homely Solutions Sàrl est la maison mère et peut être représentée par d'autres marques telles que <strong>Homely Conciergerie</strong> (marque de Homely Solutions Sàrl) par exemple.</p>
                <p className="font-semibold mt-4 text-[#2B2B2B]">Les services de gestion intégrale comprennent :</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Services de Ménage (tels que définis dans les CGV en Annexe MÉNAGE)</li>
                  <li>Services de Linge (tels que définis dans les CGV en Annexe LINGE)</li>
                  <li>Services d'Accueil (tels que définis dans les CGV Annexe ACCUEIL)</li>
                  <li>Services de Création des comptes et annonces sur les Sites Internet Sélectionnés</li>
                  <li>Services de Correspondance avec les voyageurs via messagerie électronique et/ou téléphone</li>
                </ul>
              </Section>

              <Section title="Article 2 – Rémunération">
                <p>La rémunération du Prestataire est à la charge du Propriétaire. Lorsque la prestation de service aura été effectuée, la rémunération du Prestataire deviendra immédiatement exigible.</p>
                <p className="font-semibold mt-4 text-[#2B2B2B]">Comme convenu et établi avec le Propriétaire, le Prestataire aura droit aux honoraires suivants :</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>18–25% (selon le mode de gestion) de commissions sur les recettes provenant des locations courte durée</li>
                  <li>Frais de création d'annonce : CHF 500.– OFFERT si le contrat n'est pas résilié pendant les 12 premiers mois</li>
                  <li>Photos professionnelles : CHF 350.– OFFERT si le contrat n'est pas résilié pendant les 12 premiers mois</li>
                  <li>Forfait ménage + blanchisserie payés par les voyageurs : voir article 6.2</li>
                </ul>
                <p className="font-semibold mt-6 text-[#2B2B2B]">2.1 Base de calcul de la commission</p>
                <p className="mt-2">La commission de Homely Solutions Sàrl est calculée sur la base du chiffre d'affaires brut, tel qu'il est perçu par les plateformes de réservation de type OTA (Airbnb, Booking, etc.), hors taxe de séjour et hors TVA si elle est applicable. Le chiffre d'affaires brut comprend l'ensemble des sommes payées par les voyageurs, y compris les frais de ménage, les frais de service, ou toute autre charge refacturée, à l'exception des montants explicitement mentionnés comme taxe de séjour ou TVA.</p>
              </Section>

              <Section title="Article 3 – Dispositions Générales">
                <p>Les présentes conditions générales s'appliquent à toute souscription effectuée par le Propriétaire en qualité d'hôte auprès de Homely Solutions Sàrl, enregistrée au Registre du Commerce du canton de Vaud sous le numéro CHE-418.697.448 dont le siège social est situé en Suisse.</p>
                <p className="mt-4">Dans le cadre des présentes conditions générales, le terme « <strong>Hôte</strong> » désigne les Clients qui agissent en tant que tel. Le terme « <strong>Voyageur</strong> » désigne toute personne qui loue le logement d'un Hôte.</p>
                <p className="mt-4">Homely Solutions Sàrl fournit un service à l'Hôte afin que son logement soit prêt à accueillir les Voyageurs. Les services fournis sont complémentaires et distincts des « Sites Internet Sélectionnés » (Airbnb, Booking, etc.).</p>
              </Section>

              <Section title="Article 4 – Le Service">
                <p>Sous réserve de la réception d'une Demande de Service valide, Homely Solutions Sàrl consent à fournir au Client un « Service de Gestion Intégrale ». Une demande peut être formulée par mail : <a href="mailto:info@homely-solutions.com" className="text-[#8A9474] hover:underline">info@homely-solutions.com</a>.</p>
                <p className="mt-4">Elle devra être soumise au moins <strong>72 heures</strong> avant la réalisation du service. Toute demande dans un délai plus court fera l'objet de frais supplémentaires.</p>
                <p className="mt-4">Le présent contrat confère à Homely Solutions Sàrl une <strong>obligation de moyens et non de résultat</strong>. Homely Solutions Sàrl ne pourra être tenu responsable des dommages qui pourraient être causés au Logement ou à son contenu pendant la durée de ce contrat, sous réserve des règles légales sur la faute grave et le dol.</p>
              </Section>

              <Section title="Article 5 – Obligations du Client">
                <p className="font-semibold text-[#2B2B2B]">5.1 Obligations générales</p>
                <p className="mt-2">Le Client s'engage à fournir des informations complètes sur le Logement, coopérer pleinement, fournir un minimum de 3 jeux de clés, souscrire une assurance habitation appropriée et indemniser Homely Solutions Sàrl contre toute réclamation résultant de la violation des présentes conditions.</p>
                
                <p className="font-semibold text-[#2B2B2B] mt-6">5.2 Autorité exclusive sur les réservations et les prix</p>
                <p className="mt-2">Homely Solutions Sàrl est <strong>seule et exclusivement compétente</strong> pour accepter ou refuser les réservations et fixer les prix selon une tarification dynamique. Le Propriétaire n'a aucun droit d'intervenir directement.</p>
                
                <p className="font-semibold text-[#2B2B2B] mt-6">5.3 Séjour personnel du Propriétaire</p>
                <p className="mt-2">Le Propriétaire dispose d'un droit de séjour personnel dans son logement dans la limite de <strong>30 nuits par année civile</strong>. Il lui appartient de bloquer les dates souhaitées via l'application mise à sa disposition.</p>
                
                <p className="font-semibold text-[#2B2B2B] mt-6">5.4 Non-contact des voyageurs et non-débauchage</p>
                <p className="mt-2">Le Propriétaire s'interdit de contacter directement les voyageurs obtenus par Homely Solutions Sàrl et de débaucher les collaborateurs ou prestataires de Homely Solutions Sàrl pendant la durée du contrat et 12 mois après sa cessation.</p>
              </Section>

              <Section title="Article 6 – Commissions et Rémunération">
                <p className="font-semibold text-[#2B2B2B]">6.1 Honoraire de gestion</p>
                <p className="mt-2">Homely Solutions Sàrl percevra une commission de gestion en pourcentage des revenus locatifs. Le détail figurera dans le contrat.</p>
                
                <p className="font-semibold text-[#2B2B2B] mt-6">6.2 Frais par location</p>
                <p className="mt-2">Outre les honoraires de gestion, Homely Solutions Sàrl facture pour chaque location des frais de préparation du Logement et de ménage. Ces frais sont, dans la mesure du possible, refacturés aux voyageurs.</p>
                
                <p className="font-semibold text-[#2B2B2B] mt-6">6.7 Relevés de comptes</p>
                <p className="mt-2">Un relevé mensuel récapitulant les réservations, commissions et montant net dû est disponible sur l'application et adressé par e‑mail entre le 25 et le 30 de chaque mois. Le Propriétaire dispose de <strong>10 jours calendaires</strong> pour contester le relevé. Passé ce délai, le relevé est réputé validé.</p>
              </Section>

              <Section title="Article 7 – Responsabilité">
                <p>Homely Solutions Sàrl répond des dommages causés au Propriétaire uniquement en cas de violation contractuelle fautive. Sa responsabilité est limitée au montant total des commissions effectivement versées au cours des douze (12) derniers mois, sauf dol ou faute grave.</p>
                <p className="mt-4">Homely Solutions Sàrl n'encourt aucune responsabilité pour les détériorations causées par les Voyageurs, les faits relatifs au comportement des Voyageurs, les défaillances de prestataires tiers ou l'état et la sécurité du Logement.</p>
              </Section>

              <Section title="Article 8 – Durée">
                <p>Le présent contrat est conclu pour une durée initiale de <strong>12 mois</strong> à compter de sa signature. Il se renouvelle tacitement par périodes successives de 12 mois, sauf résiliation.</p>
                <p className="mt-4">Conformément à l'article 404 CO, chaque Partie peut résilier le mandat en tout temps par notification écrite (y compris e‑mail).</p>
                <p className="mt-4">En cas de résiliation avant l'écoulement de 12 mois et/ou avant 40 nuits louées, le Propriétaire verse une indemnité forfaitaire de <strong>CHF 500.– TTC</strong> couvrant les frais initiaux offerts.</p>
              </Section>

              <Section title="Article 9 – Divers">
                <p>Les présentes conditions générales constituent l'intégralité de l'accord des Parties. Aucune modification ne sera valable sans accord écrit et signé des Parties.</p>
              </Section>

              <Section title="Article 10 – Protection des Données (RGPD / nLPD)">
                <p>Homely Solutions Sàrl traite des données personnelles relatives au Propriétaire et aux voyageurs conformément au droit suisse (nLPD) et au RGPD lorsqu'applicable.</p>
                <p className="mt-4">Les personnes concernées disposent de droits d'accès, de rectification, d'effacement et d'opposition qu'elles peuvent exercer en écrivant à : <a href="mailto:info@homely-solutions.com" className="text-[#8A9474] hover:underline">info@homely-solutions.com</a>.</p>
                
                <p className="font-semibold text-[#2B2B2B] mt-6">10.1 Propriété intellectuelle</p>
                <p className="mt-2">Toutes les photographies, textes d'annonces, guides digitaux et contenus créés par Homely Solutions Sàrl demeurent sa propriété exclusive, y compris après la fin du contrat.</p>
              </Section>

              <Section title="Article 11 – Règlement des Litiges">
                <p>En cas de litige, les Parties s'engagent à tenter de le résoudre à l'amiable dans un délai de 30 jours. À défaut, tout litige est soumis à la compétence exclusive des tribunaux du siège de Homely Solutions Sàrl, soit Yverdon-les-Bains (canton de Vaud, Suisse).</p>
              </Section>

              <Section title="Article 12 – Droit Applicable">
                <p>Les présentes conditions générales sont soumises au droit suisse.</p>
              </Section>

              <Section title="Annexe MÉNAGE">
                <p className="font-semibold text-[#2B2B2B]">Services effectués avant le séjour</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Retrait de tous les déchets et nettoyage ordinaire de toute la surface du Logement</li>
                  <li>Nettoyage de la salle de bain, lavabos et toilettes</li>
                  <li>Faire les lits et passer l'aspirateur</li>
                  <li>Fourniture minimum de savon, papier toilette et café</li>
                  <li>Contrôle visuel final du Logement</li>
                  <li>Nettoyage des vitres jusqu'à 2 mètres de hauteur</li>
                </ul>
                <p className="mt-4"><strong>Services exclus :</strong> nettoyage des murs, des meubles, fenêtres extérieures, jardinage, nettoyage industriel, déplacement de meubles lourds, extermination d'insectes.</p>
              </Section>

              <Section title="Annexe LINGE">
                <p>Le Client s'engage à fournir pour chaque réservation :</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Un drap nettoyé par lit</li>
                  <li>Une housse de couette par lit</li>
                  <li>Un nombre approprié de taies d'oreiller</li>
                  <li>Une serviette de bain par Voyageur</li>
                  <li>Une serviette à main par salle de bain</li>
                  <li>Un tapis de bain par salle de bain</li>
                  <li>Un torchon pour la cuisine</li>
                </ul>
              </Section>

              <Section title="Annexe ACCUEIL">
                <p>L'accueil s'effectue de manière autonome grâce à une boîte à clés à chiffres. Des guides digitaux détaillés (texte et vidéo) permettent aux voyageurs d'accéder au logement en totale autonomie.</p>
                <p className="mt-4">Notre équipe est disponible de 9h à 18h. Un service Premium peut être proposé en dehors de ces horaires.</p>
              </Section>

              <Section title="Annexe SITES INTERNET SÉLECTIONNÉS">
                <p>Plateformes autorisées :</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Booking.com</li>
                  <li>Airbnb (programme Garantie Hôte à hauteur de 800'000€)</li>
                  <li>D'autres plateformes sur demande</li>
                </ul>
                <p className="mt-4">En cas d'actions malveillantes des locataires, Homely Solutions Sàrl tente d'obtenir réparation mais n'est pas tenue d'intervenir en second ressort. Notre rôle est celui d'une conciergerie et non d'une assurance. Nous recommandons de souscrire une assurance complémentaire.</p>
              </Section>
            </div>

            {/* Footer du document */}
            <div className="mt-8 pt-6 border-t border-[#E5E2DB] text-center">
              <p className="text-sm text-[#8B8B8B] mb-6">
                Pour toute question concernant ces Conditions Générales de Vente, veuillez nous contacter à :<br />
                <a href="mailto:info@homely-solutions.com" className="text-[#8A9474] hover:underline">info@homely-solutions.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
